import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, apiGet, apiPost } from '../api/client.js';
import { Layout } from '../components/Layout.js';
import { Loader2Icon } from '../components/icons.js';
import { Icon } from '../components/Icon.js';

/**
 * Admin surface for the document-type taxonomy. Mirrors the structure of
 * /admin/specialisations: a list of every canonical type plus the
 * "(no type)" bucket, doc counts per type, and a footer action that
 * triggers a re-tag of stale rows.
 *
 * The taxonomy itself is a hardcoded array (`CANONICAL_DOCUMENT_TYPES` in
 * `@kennisbank/shared`) — this page is read-only over the vocabulary; the
 * only mutating action is "Clear documentType on stale docs" which lets
 * the next ingest pass re-run DocumentTypeTagger.
 */

const ERROR_FALLBACK = 'Something went wrong. Please try again.';

function errorMessage(err: unknown, fallback = ERROR_FALLBACK): string {
  if (err instanceof ApiError) return err.message ?? fallback;
  return err instanceof Error ? err.message : fallback;
}

interface TypeRow {
  type: string;
  count: number;
}

interface Summary {
  types: TypeRow[];
  untagged: number;
  totalDocs: number;
}

export function AdminDocumentTypesPage(): JSX.Element {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retagging, setRetagging] = useState(false);
  const [retagResult, setRetagResult] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet<Summary>('/api/v1/admin/document-types/summary');
      setSummary(res);
    } catch (err) {
      setError(errorMessage(err, 'Could not load document types.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => {
    if (!summary) return [] as TypeRow[];
    return [...summary.types, { type: '(no type)', count: summary.untagged }];
  }, [summary]);

  const total = summary?.totalDocs ?? 0;

  const onRetag = async (): Promise<void> => {
    setRetagging(true);
    setRetagResult(null);
    setError(null);
    try {
      const res = await apiPost<{ cleared: number }>(
        '/api/v1/admin/document-types/retag-stale',
        {},
      );
      setRetagResult(
        res.cleared === 0
          ? 'No stale rows to clear — everything is either operator-edited or already tagged.'
          : `Cleared documentType on ${res.cleared} row(s). The next ingest run will re-tag them.`,
      );
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not clear stale tags.'));
    } finally {
      setRetagging(false);
    }
  };

  return (
    <Layout>
      <div className="flex h-full min-h-0 flex-col bg-nebula-app">
        <div className="px-6 pt-6">
          <header className="mb-4">
            <h1 className="ov-h-page">Document Types</h1>
            <p className="ov-meta mt-1 max-w-3xl">
              The canonical document-type taxonomy applied to every ingested document. The list
              itself is fixed in code (changes require a release); this screen shows how the corpus
              is distributed across types and lets you re-tag rows whose type was set by the
              automatic tagger but is now stale.
            </p>
          </header>
        </div>

        {error && (
          <div className="mx-6 mt-2 rounded-md border border-nebula-error/30 bg-nebula-error-bg px-4 py-2.5 text-sm text-nebula-error">
            {error}
          </div>
        )}
        {retagResult && (
          <div className="mx-6 mt-2 rounded-md border border-nebula-info/30 bg-nebula-info-bg px-4 py-2.5 text-sm text-nebula-info">
            {retagResult}
          </div>
        )}

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden px-6 pb-6 pt-4">
          <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-nebula-border-soft bg-nebula-surface">
            <div className="flex items-center justify-between border-b border-nebula-border-soft px-5 py-3">
              <div className="flex items-center gap-2">
                <Icon name="category" size={16} className="text-nebula-secondary" />
                <h2 className="ov-h-card">Taxonomy</h2>
                <span className="text-xs text-nebula-fg-3">
                  · {summary?.types.length ?? 0} canonical types · {total} documents
                </span>
              </div>
            </div>

            <div className="thin-scroll min-h-0 flex-1 overflow-y-auto">
              {loading && !summary ? (
                <div className="flex items-center justify-center py-12 text-sm text-nebula-fg-3">
                  <Loader2Icon size={14} className="mr-2 animate-spin" />
                  Loading…
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="border-b border-nebula-border-soft bg-nebula-gray-6">
                    <tr>
                      <th className="ov-meta-upper px-5 py-2 text-left">Type</th>
                      <th className="ov-meta-upper px-5 py-2 text-right">Documents</th>
                      <th className="ov-meta-upper px-5 py-2 text-right">% of corpus</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const pct = total > 0 ? ((row.count / total) * 100).toFixed(1) + '%' : '—';
                      const isUntagged = row.type === '(no type)';
                      return (
                        <tr
                          key={row.type}
                          className="border-b border-nebula-border-soft last:border-0 hover:bg-nebula-tint"
                        >
                          <td className="px-5 py-2 align-middle">
                            <span
                              className={
                                isUntagged
                                  ? 'inline-flex items-center gap-1.5 rounded-full bg-nebula-warning-bg px-2 py-0.5 text-[12px] font-medium text-nebula-warning'
                                  : 'font-medium text-nebula-fg-1'
                              }
                            >
                              {row.type}
                            </span>
                          </td>
                          <td className="px-5 py-2 text-right align-middle font-numeric text-nebula-fg-1">
                            {row.count}
                          </td>
                          <td className="px-5 py-2 text-right align-middle font-numeric text-nebula-fg-3">
                            {pct}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-nebula-border-soft px-5 py-3">
              <p className="text-xs text-nebula-fg-3 max-w-2xl">
                Re-tagging clears <code className="font-mono">documentType</code> on every row where
                the automatic tagger was the last source of truth (operator-edited rows are left
                alone). The next ingest pass re-runs the tagger and refills the field.
              </p>
              <button
                type="button"
                onClick={() => void onRetag()}
                disabled={retagging || loading}
                className="inline-flex items-center gap-1.5 rounded-md bg-nebula-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-nebula-primary/90 disabled:opacity-50"
              >
                {retagging && <Loader2Icon size={13} className="animate-spin" />}
                Re-tag stale documents
              </button>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}
